package org.teddapp.project.ui.projects

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutHorizontally
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.awaitEachGesture
import androidx.compose.foundation.gestures.awaitFirstDown
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.input.pointer.positionChange
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInRoot
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.tedd.db.Organization
import com.tedd.db.Project
import com.tedd.db.ProjectStatus
import com.tedd.db.Task
import kotlin.math.roundToInt
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.launch
import org.teddapp.project.db.ParentKind
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.ChipSize
import org.teddapp.project.ui.ChipTone
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddChip
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddLogo
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.nav.Section
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddDateField
import org.teddapp.project.ui.TeddType
import org.teddapp.project.ui.teddClickable

// ──────────────────────────────────────────────────────────────────────
// Parties propres à la mise en page compacte : en-tête et menu, bascule de vue,
// pastilles de colonnes, couloir, liste, bouton flottant, feuilles, détail.
// ──────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────
// Action menu — pattern aligné sur le Mac ActionMenuPopup (Pomodoro).
// Carte arrondie 10dp, header uppercase, lignes 6dp arrondies, séparateur
// avant l'action destructive.
// ──────────────────────────────────────────────────────────────────────
@Composable
internal fun ProjectsCompactActionMenu(
    onNewProject: () -> Unit,
    onEditProject: (() -> Unit)?,
    onDeleteProject: (() -> Unit)?,
    onNewOrganization: () -> Unit,
) {
    Column(
        modifier = Modifier
            .width(232.dp)
            .clip(RoundedCornerShape(TeddTokens.RSm))
            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
            .padding(6.dp),
    ) {
        Text(
            text = "ACTIONS",
            color = TeddTokens.InkMuted,
            fontSize = 10.sp,
            fontWeight = FontWeight.Bold,
            letterSpacing = 0.6.sp,
            modifier = Modifier.padding(start = 8.dp, end = 8.dp, top = 5.dp, bottom = 3.dp),
        )
        PhoneMenuRow(
            label = "Nouveau projet",
            icon = "plus",
            iconColor = TeddTokens.LimeStrong,
            onClick = onNewProject,
        )
        if (onEditProject != null) {
            PhoneMenuRow(
                label = "Modifier le projet",
                icon = "edit",
                iconColor = TeddTokens.InkMuted,
                onClick = onEditProject,
            )
        }
        PhoneMenuRow(
            label = "Nouvelle organisation",
            icon = "users",
            iconColor = TeddTokens.InkMuted,
            onClick = onNewOrganization,
        )
        if (onDeleteProject != null) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.dp)
                    .padding(horizontal = 2.dp),
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .background(TeddTokens.Line),
                )
            }
            Spacer(modifier = Modifier.height(4.dp))
            PhoneMenuRow(
                label = "Supprimer le projet",
                icon = "trash",
                iconColor = TeddTokens.Danger,
                danger = true,
                onClick = onDeleteProject,
            )
        }
    }
}


@Composable
internal fun PhoneMenuRow(
    label: String,
    icon: String,
    iconColor: Color,
    danger: Boolean = false,
    onClick: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(TeddTokens.RXs))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 8.dp, vertical = 9.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier.size(14.dp),
            contentAlignment = Alignment.Center,
        ) {
            TeddIcon(name = icon, size = 13.dp, color = iconColor, strokeWidth = 1.9.dp)
        }
        Text(
            text = label,
            color = if (danger) TeddTokens.Danger else TeddTokens.Ink,
            fontSize = 13.sp,
            fontWeight = FontWeight.Medium,
        )
    }
}

// ──────────────────────────────────────────────────────────────────────
// V2 — sealed states + view enum
// ──────────────────────────────────────────────────────────────────────



// ──────────────────────────────────────────────────────────────────────
// Sub-composables
// ──────────────────────────────────────────────────────────────────────

@Composable
internal fun ProjectsCompactHeader(
    project: Project?,
    org: Organization?,
    onSelectorClick: () -> Unit,
    menuOpen: Boolean,
    onMenuToggle: () -> Unit,
    onMenuDismiss: () -> Unit,
    onNewProject: () -> Unit,
    onEditProject: (() -> Unit)?,
    onDeleteProject: (() -> Unit)?,
    onNewOrganization: () -> Unit,
) {
    val orgColor = org?.let { runCatching { Color(parseHexProjPhone(it.color)) }.getOrNull() }
    val projColor = project?.let { runCatching { Color(parseHexProjPhone(it.color)) }.getOrNull() }
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(TeddTokens.Bg)
            .statusBarsPadding()
            .padding(start = 16.dp, end = 16.dp, top = 14.dp, bottom = 12.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        // Le projet ouvert, en carte : on la touche pour en changer.
        val shape = RoundedCornerShape(TeddTokens.RMd)
        Row(
            modifier = Modifier
                .weight(1f)
                .border(1.dp, TeddTokens.Line, shape)
                .teddClickable(onClick = onSelectorClick, shape = shape, fill = TeddTokens.Surface, hover = TeddTokens.Surface, role = null)
                .padding(horizontal = 12.dp, vertical = 10.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Box(
                modifier = Modifier.size(40.dp).background(TeddTokens.SurfaceSunken, RoundedCornerShape(TeddTokens.RSm)),
                contentAlignment = Alignment.Center,
            ) {
                TeddIcon(name = project?.icon ?: "grid", size = 20.dp, color = projColor ?: TeddTokens.InkMuted)
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(org?.name ?: "Aucune organisation", color = orgColor ?: TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 1)
                Text(
                    text = project?.name ?: "Aucun projet",
                    color = TeddTokens.Ink,
                    style = TeddType.Title2.copy(fontSize = 18.sp, lineHeight = 24.sp),
                    maxLines = 1,
                    overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis,
                )
            }
            TeddIcon(name = "chevron-down", size = 16.dp, color = TeddTokens.InkMuted)
        }
        Box {
            TeddIconButton(onClick = onMenuToggle, size = 40.dp, active = menuOpen) {
                TeddIcon(name = "more", size = 18.dp, color = TeddTokens.InkMuted)
            }
            if (menuOpen) {
                androidx.compose.ui.window.Popup(
                    alignment = Alignment.TopEnd,
                    offset = androidx.compose.ui.unit.IntOffset(0, 44),
                    onDismissRequest = onMenuDismiss,
                    properties = androidx.compose.ui.window.PopupProperties(focusable = true),
                ) {
                    ProjectsCompactActionMenu(
                        onNewProject = onNewProject,
                        onEditProject = onEditProject,
                        onDeleteProject = onDeleteProject,
                        onNewOrganization = onNewOrganization,
                    )
                }
            }
        }
    }
}

@Composable
internal fun ProjectsCompactViewToggle(value: ProjectView, onChange: (ProjectView) -> Unit) {
    Box(modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp)) {
        ProjectViewToggle(value = value, onChange = onChange)
    }
}

@Composable
internal fun ProjectsCompactStatusPills(
    statuses: List<ProjectStatus>,
    currentPage: Int,
    tasksByStatus: (String) -> Int,
    onSelect: (Int) -> Unit,
    onLongPress: (ProjectStatus) -> Unit,
    onAdd: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = 16.dp, vertical = 4.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        statuses.forEachIndexed { index, status ->
            val color = runCatching { Color(parseHexProjPhone(status.color)) }.getOrElse { TeddTokens.InkMuted }
            val selected = index == currentPage
            val shape = RoundedCornerShape(TeddTokens.RFull)
            // La colonne affichée se pose sur la surface, cerclée d'encre ; sa couleur reste une pastille.
            Row(
                modifier = Modifier
                    .clip(shape)
                    .background(if (selected) TeddTokens.Surface else Color.Transparent, shape)
                    .border(1.dp, if (selected) TeddTokens.Ink else TeddTokens.Line, shape)
                    .pointerInput(status.id) {
                        detectTapGestures(
                            onTap = { onSelect(index) },
                            onLongPress = { onLongPress(status) },
                        )
                    }
                    .height(34.dp)
                    .padding(horizontal = 12.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                Box(modifier = Modifier.size(8.dp).background(color, RoundedCornerShape(2.dp)))
                Text(
                    text = status.name,
                    color = if (selected) TeddTokens.Ink else TeddTokens.InkMuted,
                    style = TeddType.Label.copy(fontWeight = FontWeight(if (selected) 600 else 450)),
                    maxLines = 1,
                )
                Text("${tasksByStatus(status.id)}", color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
            }
        }
        // Ajouter une colonne.
        TeddIconButton(onClick = onAdd, size = 34.dp) {
            TeddIcon(name = "plus", size = 16.dp, color = TeddTokens.InkMuted)
        }
    }
}

@Composable
internal fun ProjectsCompactListFilters(
    statuses: List<ProjectStatus>,
    statusFilter: String?,
    onStatusFilter: (String?) -> Unit,
    priorityFilter: String?,
    onPriorityFilter: (String?) -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .horizontalScroll(rememberScrollState())
            .padding(horizontal = 12.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        ProjectsCompactFilterPill("Tous", active = statusFilter == null, color = TeddTokens.LimeStrong) { onStatusFilter(null) }
        statuses.forEach { s ->
            val c = runCatching { Color(parseHexProjPhone(s.color)) }.getOrElse { TeddTokens.LimeStrong }
            ProjectsCompactFilterPill(s.name, active = statusFilter == s.id, color = c) {
                onStatusFilter(if (statusFilter == s.id) null else s.id)
            }
        }
        Box(modifier = Modifier.width(1.dp).height(18.dp).background(TeddTokens.Line))
        listOf("high", "medium", "low").forEach { p ->
            ProjectsCompactFilterPill(priorityLabel(p), active = priorityFilter == p, color = priorityColor(p)) {
                onPriorityFilter(if (priorityFilter == p) null else p)
            }
        }
    }
}

@Composable
internal fun ProjectsCompactFilterPill(label: String, active: Boolean, color: Color, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(TeddTokens.RFull))
            .background(if (active) color.copy(alpha = 0.12f) else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RFull))
            .border(1.dp, if (active) color else TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            )
            .padding(horizontal = 11.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Text(
            text = label,
            color = if (active) color else TeddTokens.InkMuted,
            fontSize = 12.sp,
            fontWeight = if (active) FontWeight.SemiBold else FontWeight.Medium,
            maxLines = 1,
        )
    }
}

@Composable
internal fun ProjectsCompactListView(
    statuses: List<ProjectStatus>,
    tasks: List<Task>,
    onOpenTask: (String) -> Unit,
    onToggleDone: (Task) -> Unit,
    onSendToPomodoro: (Task) -> Unit,
    modifier: Modifier = Modifier,
) {
    var statusFilter by remember(statuses) { mutableStateOf<String?>(null) }
    var priorityFilter by remember { mutableStateOf<String?>(null) }
    val filteredTasks = tasks.filter { priorityFilter == null || (it.priority ?: "medium") == priorityFilter }
    val shownStatuses = if (statusFilter == null) statuses else statuses.filter { it.id == statusFilter }

    Column(modifier = modifier.fillMaxWidth()) {
        ProjectsCompactListFilters(
            statuses = statuses,
            statusFilter = statusFilter,
            onStatusFilter = { statusFilter = it },
            priorityFilter = priorityFilter,
            onPriorityFilter = { priorityFilter = it },
        )
        LazyColumn(
            modifier = Modifier.weight(1f).fillMaxWidth().padding(horizontal = 12.dp),
            contentPadding = androidx.compose.foundation.layout.PaddingValues(bottom = 96.dp, top = 4.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            shownStatuses.forEach { status ->
                val statusTasks = filteredTasks.filter { it.status_id == status.id }.sortedBy { it.order_index }
                if (statusTasks.isEmpty()) return@forEach
            val color = runCatching { Color(parseHexProjPhone(status.color)) }.getOrElse { Color(0xFF6366F1) }
            item(key = "h-${status.id}") {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(start = 4.dp, top = 12.dp, bottom = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    Box(
                        modifier = Modifier
                            .size(8.dp)
                            .clip(RoundedCornerShape(2.dp))
                            .background(color),
                    )
                    Text(
                        status.name.uppercase(),
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                        fontWeight = FontWeight.Bold,
                        letterSpacing = 0.5.sp,
                    )
                    Text(
                        "${statusTasks.size}",
                        color = TeddTokens.InkMuted,
                        fontSize = 11.sp,
                    )
                }
            }
            items(statusTasks, key = { it.id }) { task ->
                ProjectsCompactTaskCard(
                    task = task,
                    statusColor = color,
                    onClick = { onOpenTask(task.id) },
                    onToggleDone = { onToggleDone(task) },
                    onSendToPomodoro = { onSendToPomodoro(task) },
                )
            }
            }
        }
    }
}



@Composable
internal fun ProjectsCompactSwimlane(
    status: ProjectStatus,
    tasks: List<Task>,
    onOpenTask: (String) -> Unit,
    onToggleTaskDone: (Task) -> Unit,
    onSendToPomodoro: ((Task) -> Unit)? = null,
) {
    val color = runCatching { Color(parseHexProjPhone(status.color)) }.getOrElse { TeddTokens.LimeStrong }
    if (tasks.isEmpty()) {
        Box(
            modifier = Modifier.fillMaxSize().padding(28.dp),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = "Aucune tâche dans « ${status.name} »",
                color = TeddTokens.InkMuted,
                style = TeddType.Body,
            )
        }
        return
    }
    LazyColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = androidx.compose.foundation.layout.PaddingValues(horizontal = 16.dp, vertical = 8.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        items(tasks, key = { it.id }) { task ->
            ProjectsCompactTaskCard(
                task = task,
                statusColor = color,
                onClick = { onOpenTask(task.id) },
                onToggleDone = { onToggleTaskDone(task) },
                onSendToPomodoro = onSendToPomodoro?.let { fn -> { fn(task) } },
            )
        }
    }
}

/** Carte d'une tâche sur téléphone : la même que sur ordinateur, avec la case pour la terminer. */
@Composable
internal fun ProjectsCompactTaskCard(
    task: Task,
    @Suppress("UNUSED_PARAMETER") statusColor: Color,
    onClick: () -> Unit,
    onToggleDone: () -> Unit,
    onSendToPomodoro: (() -> Unit)? = null,
) {
    val done = task.status == "done"
    val shape = RoundedCornerShape(TeddTokens.RMd)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(TeddTokens.Surface, shape)
            .border(1.dp, TeddTokens.Line, shape)
            .clip(shape)
            .clickable(interactionSource = remember { MutableInteractionSource() }, indication = null, onClick = onClick)
            .padding(start = 12.dp, end = 6.dp, top = 12.dp, bottom = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        org.teddapp.project.ui.TeddCheckbox(checked = done, onCheckedChange = { onToggleDone() }, modifier = Modifier.padding(top = 1.dp))
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(
                text = task.title,
                color = if (done) TeddTokens.InkMuted else TeddTokens.Ink,
                style = TeddType.Label.copy(
                    fontSize = 15.sp,
                    lineHeight = 21.sp,
                    textDecoration = if (done) androidx.compose.ui.text.style.TextDecoration.LineThrough else null,
                ),
            )
            if (!task.description.isNullOrBlank()) {
                Text(task.description!!, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 2, overflow = androidx.compose.ui.text.style.TextOverflow.Ellipsis)
            }
            val high = task.priority == "high" && !done
            val due = task.due_date?.takeIf { it.isNotBlank() }
            val late = due != null && due < TeddDates.todayIso && !done
            val estimate = estimateLabel(task.estimated_minutes, task.real_minutes)
            if (high || due != null || estimate != null) {
                Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    if (high) org.teddapp.project.ui.TeddTag("Prioritaire", tone = org.teddapp.project.ui.TeddTagTone.Danger, dot = true)
                    if (due != null) {
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                            TeddIcon(name = if (late) "alert" else "calendar", size = 12.dp, color = if (late) TeddTokens.Warning else TeddTokens.InkMuted)
                            Text(TeddDates.formatRelative(due) ?: due, color = if (late) TeddTokens.Warning else TeddTokens.InkMuted, style = TeddType.Caption)
                        }
                    }
                    if (estimate != null) Text(estimate, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp))
                }
            }
        }
        if (onSendToPomodoro != null && !done) {
            // Envoyer la tâche dans la liste Focus du jour.
            TeddIconButton(onClick = onSendToPomodoro, size = 36.dp) {
                TeddIcon(name = "focus", size = 18.dp, color = TeddTokens.InkMuted)
            }
        }
    }
}

@Composable
internal fun FabAddTask(modifier: Modifier = Modifier, onClick: () -> Unit) {
    Box(
        modifier = modifier
            .size(60.dp)
            .clip(CircleShape)
            .background(TeddTokens.SignatureGradient, CircleShape)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClick,
            ),
        contentAlignment = Alignment.Center,
    ) {
        TeddIcon(name = "plus", size = 22.dp, color = TeddTokens.OnAction, strokeWidth = 2.4.dp)
    }
}

// ──────────────────────────────────────────────────────────────────────
// Project selector bottom sheet
// ──────────────────────────────────────────────────────────────────────
@Composable
internal fun ProjectSelectorSheet(
    organizations: List<Organization>,
    projects: List<Project>,
    selectedProjectId: String?,
    repos: TeddRepositories,
    onSelect: (Project) -> Unit,
    onClose: () -> Unit,
    onNewProjectInOrg: (String) -> Unit,
    onNewOrg: () -> Unit,
    onEditOrg: (Organization) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(max = 600.dp)
            .clip(RoundedCornerShape(20.dp, 20.dp, 0.dp, 0.dp))
            .background(TeddTokens.Bg, RoundedCornerShape(20.dp, 20.dp, 0.dp, 0.dp))
            .border(1.dp, TeddTokens.Line, RoundedCornerShape(20.dp, 20.dp, 0.dp, 0.dp))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = {},
            ),
    ) {
        // Drag handle visual
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = 8.dp, bottom = 4.dp),
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier
                    .size(40.dp, 4.dp)
                    .clip(RoundedCornerShape(2.dp))
                    .background(TeddTokens.Line),
            )
        }
        // Title
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 22.dp, vertical = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text = "Choisir un projet",
                color = TeddTokens.Ink,
                fontSize = 16.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier.weight(1f),
            )
            TeddIconButton(onClick = onNewOrg, size = 30.dp) {
                TeddIcon(name = "users", size = 14.dp, color = TeddTokens.LimeStrong)
            }
            Spacer(modifier = Modifier.width(4.dp))
            TeddIconButton(onClick = onClose, size = 30.dp) {
                Box(modifier = Modifier.rotate(45f)) {
                    TeddIcon(name = "plus", size = 14.dp, color = TeddTokens.InkMuted)
                }
            }
        }
        // List
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f, fill = false)
                .verticalScroll(rememberScrollState())
                .padding(horizontal = 16.dp, vertical = 4.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            organizations.forEach { org ->
                val color = runCatching { Color(parseHexProjPhone(org.color)) }.getOrElse { TeddTokens.LimeStrong }
                val orgProjects = projects.filter { it.organization_id == org.id }
                Column {
                    Row(
                        modifier = Modifier.padding(start = 6.dp, bottom = 6.dp).fillMaxWidth(),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Box(
                            modifier = Modifier
                                .size(20.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .background(color.copy(alpha = 0.12f), RoundedCornerShape(TeddTokens.RXs)),
                            contentAlignment = Alignment.Center,
                        ) {
                            TeddIcon(name = org.icon, size = 12.dp, color = color, strokeWidth = 2.dp)
                        }
                        Text(
                            text = org.name.uppercase(),
                            color = TeddTokens.InkMuted,
                            fontSize = 11.sp,
                            fontWeight = FontWeight.SemiBold,
                            letterSpacing = 0.6.sp,
                            modifier = Modifier.weight(1f),
                        )
                        // Bouton "+ projet" dans cette org
                        Box(
                            modifier = Modifier
                                .size(24.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .background(TeddTokens.LimeSoft, RoundedCornerShape(TeddTokens.RXs))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onNewProjectInOrg(org.id) },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            TeddIcon(name = "plus", size = 11.dp, color = TeddTokens.LimeStrong, strokeWidth = 2.4.dp)
                        }
                        Box(
                            modifier = Modifier
                                .size(24.dp)
                                .clip(RoundedCornerShape(TeddTokens.RXs))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = { onEditOrg(org) },
                                ),
                            contentAlignment = Alignment.Center,
                        ) {
                            TeddIcon(name = "edit", size = 11.dp, color = TeddTokens.InkMuted, strokeWidth = 1.8.dp)
                        }
                    }
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        orgProjects.forEach { proj ->
                            val pColor = runCatching { Color(parseHexProjPhone(proj.color)) }.getOrElse { TeddTokens.LimeStrong }
                            val selected = proj.id == selectedProjectId
                            Row(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clip(RoundedCornerShape(TeddTokens.RSm))
                                    .background(
                                        if (selected) pColor.copy(alpha = 0.12f) else TeddTokens.Surface,
                                        RoundedCornerShape(TeddTokens.RSm),
                                    )
                                    .border(
                                        1.dp,
                                        if (selected) pColor else TeddTokens.Line,
                                        RoundedCornerShape(TeddTokens.RSm),
                                    )
                                    .clickable(
                                        interactionSource = remember { MutableInteractionSource() },
                                        indication = null,
                                        onClick = { onSelect(proj) },
                                    )
                                    .padding(horizontal = 12.dp, vertical = 10.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(10.dp),
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(28.dp)
                                        .clip(RoundedCornerShape(TeddTokens.RSm))
                                        .background(
                                            Brush.linearGradient(listOf(pColor, pColor.copy(alpha = 0.66f))),
                                            RoundedCornerShape(TeddTokens.RSm),
                                        ),
                                    contentAlignment = Alignment.Center,
                                ) {
                                    TeddIcon(name = proj.icon, size = 14.dp, color = TeddTokens.OnAction, strokeWidth = 2.dp)
                                }
                                Text(
                                    text = proj.name,
                                    color = if (selected) pColor else TeddTokens.Ink,
                                    fontSize = 14.sp,
                                    fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
                                    modifier = Modifier.weight(1f),
                                )
                                if (selected) {
                                    Box(
                                        modifier = Modifier
                                            .size(20.dp)
                                            .clip(CircleShape)
                                            .background(pColor, CircleShape),
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        TeddIcon(name = "check", size = 12.dp, color = TeddTokens.OnAction, strokeWidth = 2.5.dp)
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        Spacer(modifier = Modifier.height(16.dp))
    }
}

// ──────────────────────────────────────────────────────────────────────
// Quick add task sheet (inline overlay)
// ──────────────────────────────────────────────────────────────────────
@Composable
internal fun QuickAddTaskSheet(
    projectId: String,
    statusId: String,
    onCreate: (String) -> Unit,
    onClose: () -> Unit,
) {
    var title by remember { mutableStateOf("") }
    val canCreate = title.trim().isNotEmpty()

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black.copy(alpha = 0.42f))
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = onClose,
            ),
        contentAlignment = Alignment.BottomCenter,
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(20.dp, 20.dp, 0.dp, 0.dp))
                .background(TeddTokens.Bg, RoundedCornerShape(20.dp, 20.dp, 0.dp, 0.dp))
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(20.dp, 20.dp, 0.dp, 0.dp))
                .clickable(
                    interactionSource = remember { MutableInteractionSource() },
                    indication = null,
                    onClick = {},
                )
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp),
        ) {
            Text(
                text = "NOUVELLE TÂCHE",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.6.sp,
            )
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(42.dp)
                    .clip(RoundedCornerShape(TeddTokens.RSm))
                    .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                    .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                    .padding(horizontal = 12.dp),
                contentAlignment = Alignment.CenterStart,
            ) {
                androidx.compose.foundation.text.BasicTextField(
                    value = title,
                    onValueChange = { title = it },
                    singleLine = true,
                    textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 14.sp),
                    cursorBrush = androidx.compose.ui.graphics.SolidColor(TeddTokens.LimeStrong),
                    decorationBox = { inner ->
                        if (title.isEmpty()) {
                            Text("Titre de la tâche…", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                        }
                        inner()
                    },
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = onClose,
                        )
                        .padding(vertical = 12.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text("Annuler", color = TeddTokens.Ink, fontSize = 13.sp, fontWeight = FontWeight.Medium)
                }
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(
                            if (canCreate) TeddTokens.SignatureGradient else Brush.horizontalGradient(
                                listOf(TeddTokens.InkSubtle, TeddTokens.InkSubtle)
                            ),
                            RoundedCornerShape(TeddTokens.RSm),
                        )
                        .clickable(
                            enabled = canCreate,
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null,
                            onClick = { onCreate(title.trim()) },
                        )
                        .padding(vertical = 12.dp),
                    contentAlignment = Alignment.Center,
                ) {
                    Text("Créer", color = TeddTokens.OnAction, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

// ──────────────────────────────────────────────────────────────────────
// Task detail full-screen modal
// ──────────────────────────────────────────────────────────────────────
@Composable
internal fun ProjectsCompactTaskDetail(
    task: Task,
    statuses: List<ProjectStatus>,
    onClose: () -> Unit,
    onUpdate: (title: String, description: String, statusId: String?, priority: String?, dueDate: String) -> Unit,
    onDelete: () -> Unit,
) {
    var title by remember(task.id) { mutableStateOf(task.title) }
    var description by remember(task.id) { mutableStateOf(task.description ?: "") }
    var statusId by remember(task.id) { mutableStateOf(task.status_id) }
    var priority by remember(task.id) { mutableStateOf(task.priority ?: "medium") }
    var dueDate by remember(task.id) { mutableStateOf(task.due_date ?: "") }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .background(TeddTokens.Bg)
            .clickable(
                interactionSource = remember { MutableInteractionSource() },
                indication = null,
                onClick = {},
            ),
    ) {
        // Header
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(TeddTokens.Bg)
                .border(1.dp, TeddTokens.Line, RoundedCornerShape(0.dp))
                .statusBarsPadding()
            .padding(start = 16.dp, end = 16.dp, top = 14.dp, bottom = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            TeddIconButton(onClick = onClose, size = 36.dp) {
                Box(modifier = Modifier.rotate(45f)) {
                    TeddIcon(name = "plus", size = 16.dp, color = TeddTokens.InkMuted, strokeWidth = 2.dp)
                }
            }
            Spacer(modifier = Modifier.width(8.dp))
            Text(
                text = "TÂCHE",
                color = TeddTokens.InkMuted,
                fontSize = 11.sp,
                fontWeight = FontWeight.SemiBold,
                letterSpacing = 0.6.sp,
                modifier = Modifier.weight(1f),
            )
            TeddIconButton(onClick = onDelete, size = 36.dp) {
                TeddIcon(name = "trash", size = 16.dp, color = TeddTokens.InkMuted)
            }
        }
        Column(
            modifier = Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(20.dp),
            verticalArrangement = Arrangement.spacedBy(18.dp),
        ) {
            androidx.compose.foundation.text.BasicTextField(
                value = title,
                onValueChange = { title = it; onUpdate(it, description, statusId, priority, dueDate) },
                textStyle = TextStyle(
                    color = TeddTokens.Ink,
                    fontSize = 22.sp,
                    fontWeight = FontWeight.SemiBold,
                    letterSpacing = (-0.3).sp,
                ),
                cursorBrush = androidx.compose.ui.graphics.SolidColor(TeddTokens.LimeStrong),
            )

            Column {
                Text("Description", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                Spacer(modifier = Modifier.height(6.dp))
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .heightIn(min = 100.dp)
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                        .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                        .padding(12.dp),
                    contentAlignment = Alignment.TopStart,
                ) {
                    androidx.compose.foundation.text.BasicTextField(
                        value = description,
                        onValueChange = {
                            description = it
                            onUpdate(title, it, statusId, priority, dueDate)
                        },
                        textStyle = TextStyle(color = TeddTokens.Ink, fontSize = 14.sp, lineHeight = 20.sp),
                        cursorBrush = androidx.compose.ui.graphics.SolidColor(TeddTokens.LimeStrong),
                        decorationBox = { inner ->
                            if (description.isEmpty()) {
                                Text("Décris ce qu'il faut faire…", color = TeddTokens.InkSubtle, fontSize = 14.sp)
                            }
                            inner()
                        },
                    )
                }
            }

            Column {
                Text("Statut", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                Spacer(modifier = Modifier.height(8.dp))
                Row(
                    modifier = Modifier.horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(6.dp),
                ) {
                    statuses.forEach { s ->
                        val sColor = runCatching { Color(parseHexProjPhone(s.color)) }.getOrElse { TeddTokens.LimeStrong }
                        val selected = statusId == s.id
                        Row(
                            modifier = Modifier
                                .clip(RoundedCornerShape(TeddTokens.RFull))
                                .background(
                                    if (selected) sColor.copy(alpha = 0.15f) else TeddTokens.Surface,
                                    RoundedCornerShape(TeddTokens.RFull),
                                )
                                .border(1.dp, if (selected) sColor else TeddTokens.Line, RoundedCornerShape(TeddTokens.RFull))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = {
                                        statusId = s.id
                                        onUpdate(title, description, s.id, priority, dueDate)
                                    },
                                )
                                .padding(horizontal = 12.dp, vertical = 8.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(6.dp),
                        ) {
                            Box(modifier = Modifier.size(8.dp).clip(CircleShape).background(sColor))
                            Text(
                                s.name,
                                color = if (selected) sColor else TeddTokens.Ink,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                    }
                }
            }

            Column {
                Text("Priorité", color = TeddTokens.InkMuted, fontSize = 12.sp, fontWeight = FontWeight.SemiBold)
                Spacer(modifier = Modifier.height(8.dp))
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    listOf("low", "medium", "high").forEach { p ->
                        val selected = priority == p
                        val c = priorityColor(p)
                        Box(
                            modifier = Modifier
                                .weight(1f)
                                .clip(RoundedCornerShape(TeddTokens.RSm))
                                .background(if (selected) c.copy(alpha = 0.12f) else TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                                .border(1.dp, if (selected) c else TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = {
                                        priority = p
                                        onUpdate(title, description, statusId, p, dueDate)
                                    },
                                )
                                .padding(vertical = 10.dp),
                            contentAlignment = Alignment.Center,
                        ) {
                            Text(
                                priorityLabel(p),
                                color = if (selected) c else TeddTokens.InkMuted,
                                fontSize = 13.sp,
                                fontWeight = FontWeight.Medium,
                            )
                        }
                    }
                }
            }

                        TeddDateField(
                label = "Échéance",
                value = dueDate,
                onValueChange = {
                    dueDate = it
                    onUpdate(title, description, statusId, priority, it)
                },
                )
        }
    }
}

internal fun parseHexProjPhone(hex: String): Long {
    val clean = hex.trimStart('#')
    val rgb = clean.toLong(16)
    return 0xFF000000L or rgb
}
